/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { GridConfig } from '@coursera-web/cds-labs';

import { isRightToLeft } from 'js/lib/language';

import { Button, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { ShowMoreGridSection } from 'bundles/cds-labs/components';
import TrackedBasicCard, {
  type TrackedBasicCardEventingData,
} from 'bundles/cds-labs/components/Modules/CannedCollectionModule/TrackedBasicCard';
import type { RenderCustomPrimaryCtaProps } from 'bundles/cds-labs/components/Modules/HeroBentoModule';
import ResponsiveImage from 'bundles/cds-labs/components/Modules/HeroBentoModule/ResponsiveImage';
import ModuleLayout, { noImage } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Eyebrow from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import Image, { type ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmLink from 'bundles/cds-labs/components/Modules/components/PreservedUtmLink';
import {
  getColorFromColorName,
  getCustomBackgroundStyle,
  parseTextColorOverride,
  resolveIsDarkBackground,
} from 'bundles/cds-labs/components/Modules/utils/colors';

import _t from 'i18n!nls/cds-labs';

const CARD_SIZE = { SMALL: 'small', REGULAR: 'regular', LARGE: 'large' } as const;
const XXS_BREAKPOINT = 450; // pixel width for extra extra small screens

export type CardSize = (typeof CARD_SIZE)[keyof typeof CARD_SIZE];
export type ColorChoice =
  | 'blue'
  | 'white'
  | 'black'
  | 'light-blue'
  | 'dark-blue'
  | 'grey'
  | 'dark-red'
  | 'primary-blue-xstrong'
  | 'primary-blue-xxstrong'
  | (string & {});

type VariantType = 'grid' | 'list';

export type VariantConfig = {
  sm?: VariantType;
};

// The offset is half of the width of the card
const BACKGROUND_IMAGE_LEFT_OFFSET = 335;

export type CannedCollectionModuleCard = {
  id: string;
  headline?: string;
  body?: string;
  target?: string;
  ctaText?: string;
  cardBackgroundImage?: ImageType;
  mobileCardBackgroundImage?: ImageType;
  eyebrowImageHeight?: number;
  eyebrow?: string;
  eyebrowBackground?: string;
  eyebrowImage?: ImageType;
  backgroundColor?: ColorChoice;
  customBackgroundColor?: string | null;
  textColorOverride?: string | null;
  onClick?: () => void;
  ctaVariant?: ButtonProps['variant'];
  openInNewTab?: boolean;
  renderCustomPrimaryCta?: (props: RenderCustomPrimaryCtaProps) => React.ReactNode;
  /**
   * `merchandisingModule` fields for the `view_merchandising_module` event each card fires once it
   * is fully visible. Merged over the per-card defaults (`moduleType: 'canned_collection_module'`,
   * card headline/body); the card's index is always appended automatically.
   */
  eventingData?: TrackedBasicCardEventingData;
};

type Props = {
  heading?: string;
  subheading?: string;
  cards: CannedCollectionModuleCard[];
  size: CardSize;
  color: ColorChoice;
  preloadImage?: boolean;
  lazyLoadImage?: boolean;
  showAllEntries?: boolean;
  showImageInFull?: boolean;
  variantConfig?: VariantConfig;
} & Pick<
  ModuleLayoutConfig,
  'backgroundColor' | 'removeTopPadding' | 'removeBottomPadding' | 'isPageGridContainerAppliedOnPage'
>;

const getCtaConfig = ({
  ctaVariant: inputVariant,
  isDarkBackground,
  isMobile,
  size,
}: {
  ctaVariant: ButtonProps['variant'] | undefined;
  isDarkBackground: boolean;
  isMobile: boolean;
  size: CardSize;
}): { variant: ButtonProps['variant']; iconColor: 'interactive' | 'invert'; isGhostCta: boolean } => {
  let variant = inputVariant ?? (isDarkBackground ? 'secondary' : 'primary');

  if (isDarkBackground && variant === 'ghost') {
    variant = 'ghostInvert';
  }

  if (isMobile && size === CARD_SIZE.LARGE && variant?.includes('ghost')) {
    return { variant: 'secondary', iconColor: 'interactive', isGhostCta: false };
  }

  const isSecondaryLike = variant?.includes('secondary');
  const isGhostCta = !!variant?.includes('ghost');
  const isGhostOnLight = !isDarkBackground && isGhostCta;
  const iconColor = isSecondaryLike || isGhostOnLight ? 'interactive' : 'invert';

  return { variant, iconColor, isGhostCta };
};

const styles = {
  card: css`
    color-scheme: light only;
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: var(--cds-spacing-200);
    text-decoration: none;
    overflow: hidden;

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
    }
  `,

  largeCardMd: css`
    ${breakpoints.up('md')} {
      border-radius: var(--cds-spacing-300);
    }
  `,

  cardSizeLarge: css`
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300);
    }
  `,

  cardSizeRegular: css`
    padding: var(--cds-spacing-300);
  `,

  cardSizeSmall: css`
    padding: var(--cds-spacing-200);
  `,

  cardColorBlack: css`
    background-color: var(--cds-color-neutral-primary);
    border: none;
  `,

  cardColorBlue: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
    border: none;
  `,

  cardColorWhite: css`
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-disabled-strong);
  `,

  cardTitleLarge: css`
    margin-bottom: var(--cds-spacing-50);
    margin-top: var(--cds-spacing-150);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-100);
    }
  `,

  cardTitle: css`
    margin-bottom: 2px;
  `,

  eyebrowSpacing: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  eyebrowSpacingLarge: css`
    margin-bottom: 0;
  `,

  // Eyebrow text without a pill background uses the same color as the body text.
  eyebrowColor: css`
    color: var(--cds-color-neutral-primary);
  `,

  eyebrowColorInvert: css`
    color: var(--cds-color-neutral-primary-invert);
  `,

  cta: css`
    display: flex;
    align-items: center;
    margin-top: var(--cds-spacing-150);
  `,

  ctaLarge: css`
    margin-top: var(--cds-spacing-100);
  `,

  customCtaWrapper: css`
    margin-top: var(--cds-spacing-150);
  `,

  ghostCta: css`
    padding-left: 0;
    padding-right: 0;

    &:hover,
    &:focus-visible {
      background-color: transparent;
      text-decoration: underline;
    }
  `,

  primaryButtonFocusStyle: css`
    &:focus-visible {
      text-decoration: none;
      outline: 1px solid transparent;
      outline-offset: 2px;
      box-shadow:
        0 0 0 2px var(--cds-color-interactive-stroke-primary-focus-invert),
        0 0 0 3px var(--cds-color-interactive-primary);
    }
  `,

  secondaryButtonFocusStyle: css`
    &:focus-visible {
      text-decoration: none;
      outline: 1px solid transparent;
      outline-offset: 2px;
      box-shadow:
        0 0 0 2px var(--cds-color-interactive-primary),
        0 0 0 3px var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  `,

  /*
   * "Stretched link" pattern — the heading link's ::before pseudo-element covers the
   * entire card. position: static !important ensures no ancestor repositions the link,
   * keeping the overlay anchored to the card's position: relative container.
   * See https://inclusive-components.design/cards/#:~:text=grid%2Dgap%3A%204.5rem%3B%0A%20%20%7D%0A%7D-,Calls%20to%20action,-As%20I%20said
   */
  stretchedOverlay: css`
    &:not([aria-disabled='true']) {
      position: static !important;
    }

    &::before {
      content: '';
      cursor: pointer;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
    }
  `,

  headingLink: css`
    color: inherit;
    text-decoration: none;
    outline: none;

    &:hover,
    &:focus,
    &:active {
      color: inherit;
      text-decoration: none;
    }
  `,

  /* Raise the decorative CTA above the stretched overlay so it receives hover styles natively */
  decorativeCta: css`
    position: relative;
    z-index: 1;
  `,

  /*
   * When the heading link has focus, show the focus ring on the CTA instead.
   * See https://inclusive-components.design/cards/#callstoaction and its demo as an example.
   */
  cardInteractionProxy: css`
    &:focus-within > div:last-child button {
      box-shadow:
        0 0 0 2px var(--cds-color-interactive-primary),
        0 0 0 3px var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  `,

  cardContentWrapper: css`
    width: 100%;
    height: 100%;
  `,
  cardContent: (setMaxWidth: boolean) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: ${setMaxWidth ? `${BACKGROUND_IMAGE_LEFT_OFFSET}px` : 'unset'};
    box-sizing: border-box;
  `,

  /*
   * In showImageInFull mode at sm+, the image is a sibling grid item so the
   * text column inherits the image's height. space-between would then push the
   * CTA to the bottom of that extra space; flex-start keeps it under the body.
   */
  cardContentImageInFull: css`
    justify-content: flex-start;
  `,

  backgroundImage: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
  `,

  backgroundImageInner: (offset: number, isRtl: boolean, isSingleCard: boolean = false) => css`
    position: absolute;
    bottom: 0;
    ${isSingleCard ? 'right: 0;' : `left: ${offset}px;`}

    ${breakpoints.down('sm')} {
      ${isSingleCard && 'right: unset;'}
      ${isSingleCard && `left: ${offset}px;`}
    }

    img {
      ${isRtl ? `transform: scaleX(-1);` : ''};
    }
  `,

  // cancel the y-padding since the block already provides y-padding
  showMoreGridSection: css`
    padding-top: 0;
    padding-bottom: 0;
  `,

  subheading: css`
    margin-top: var(--cds-spacing-50);
  `,

  headingContainer: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  backgroundImageContainer: css`
    pointer-events: none;
    ${breakpoints.up('md')} {
      position: absolute;
      bottom: 0;
      right: 0;
      width: calc(50% - var(--cds-spacing-100));
      height: 100%;
    }
  `,

  backgroundImageFull: (cardCount: number) => css`
    display: flex;
    height: 100%;
    flex-direction: column;
    flex-grow: 1;
    justify-content: end;

    /*
     * Override styles from LazyImgix
     * Beginning of override
     */
    > div,
    > div > div {
      height: 100%;
    }

    > div > div {
      padding-bottom: unset;
    }

    .lazyload-wrapper {
      display: flex;
      height: 100%;
      flex-direction: column;
      flex-grow: 1;
      justify-content: end;
    }

    /* End of override */

    img {
      object-fit: contain;
      object-position: right bottom;
      max-width: 100% !important;
      max-height: unset !important;
    }

    ${breakpoints.up('md')} {
      img {
        height: 100% !important;
      }
    }

    /* For tablet widths, cap the image height to 250px to prevent the card from ballooning on portrait assets.
     * Only applies to 3+ card collections to preserve original 2-card behavior.
     */
    ${breakpoints.down('sm')} {
      img {
        ${cardCount >= 3 ? `max-height: 250px !important;` : ''}
      }
    }

    /* For mobile widths, cap the image height to 416px to prevent the card from ballooning on portrait assets.
     * Only applies to 3+ card collections to preserve original 2-card behavior.
     */
    ${breakpoints.down('xs')} {
      img {
        ${cardCount >= 3 ? `max-height: 416px !important;` : ''}
      }
    }
  `,

  /* Not applied to single card collections */
  cardContentFull: (cardCount: number) => css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 100%;

    /* For 3+ card collections, use one column layout at <600px viewport widths instead of <1024px. */
    ${breakpoints.down(cardCount >= 3 ? 'xs' : 'sm')} {
      grid-template-columns: 1fr;
    }
  `,

  oneCardModuleBackgroundImageContainer: css`
    position: relative;
    pointer-events: none;
  `,

  oneCardModuleBackgroundImage: css`
    ${breakpoints.up('sm')} {
      position: absolute;
      bottom: 0;
      right: 0;
      height: 100%;
    }

    img {
      object-fit: contain;

      ${breakpoints.up('sm')} {
        height: 100%;
        width: 100%;
        object-position: right bottom;
      }

      ${breakpoints.down('xs')} {
        width: 100%;
      }
    }
  `,

  oneCardModuleContent: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 100%;

    ${breakpoints.down('xs')} {
      grid-template-columns: 1fr;
    }
  `,
};

export const CannedCollectionModule = React.forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
  const {
    heading,
    subheading,
    cards,
    size,
    color,
    preloadImage,
    lazyLoadImage,
    showAllEntries,
    showImageInFull = false,
    variantConfig,
  } = props;
  const isRtl: boolean = isRightToLeft(_t.getLocale());
  const isMobile: boolean = useMediaQuery(breakpoints.down('xs'));
  const isXxs: boolean = useMediaQuery(breakpoints.down(XXS_BREAKPOINT));
  const isOneCardCannedCollectionModule: boolean = cards.length === 1;

  // DOM reference to each card's heading anchor, keyed by card id — used when the
  // whole card is a link, so the decorative CTA button can forward clicks to it.
  const headingLinkRefs = React.useRef<Map<string, HTMLAnchorElement>>(new Map());

  // If true, cards are displayed in grid view for all viewport widths
  const isGridVariant = (variantConfig?.sm === 'grid' || showImageInFull) && size === CARD_SIZE.LARGE;

  let cardSizeStyle = styles.cardSizeRegular;
  let cardTitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'subtitleLarge';
  let cardSubtitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'bodySecondary';

  switch (size) {
    case CARD_SIZE.LARGE:
      cardSizeStyle = styles.cardSizeLarge;
      cardSubtitleVariant = 'bodyPrimary';
      cardTitleVariant = 'subtitleLarge';
      break;
    case CARD_SIZE.REGULAR:
      cardSizeStyle = styles.cardSizeRegular;
      break;
    case CARD_SIZE.SMALL:
      cardTitleVariant = 'subtitleMedium';
      break;
    default:
      cardSizeStyle = styles.cardSizeSmall;
      break;
  }

  const cardColorStyle = (cardBackgroundColor?: ColorChoice) => {
    return `background-color:
    ${getColorFromColorName(cardBackgroundColor)};
  `;
  };

  const cardBorderStyle =
    color === 'white' ? `border: 1px solid var(--cds-color-neutral-disabled-strong);` : `border: none;`;

  const renderCardContent = (item: CannedCollectionModuleCard, itemIndex: number) => {
    const backgroundImage = item.cardBackgroundImage;
    const cardBackgroundColor = item.backgroundColor ?? color;
    const isDarkBackground = resolveIsDarkBackground({
      backgroundColor: cardBackgroundColor,
      customBackgroundColor: item.customBackgroundColor,
      textColorOverride: parseTextColorOverride(item.textColorOverride),
    });
    const customBgParsed = item.customBackgroundColor
      ? getCustomBackgroundStyle(item.customBackgroundColor)
      : undefined;
    const shouldShowImageInFull = showImageInFull && backgroundImage?.url;
    const hasCustomCta = !!item.renderCustomPrimaryCta;
    // When there's a CTA target and no custom CTA, use the whole card as link
    const isWholeCardRenderedAsLink = !!item.target && !hasCustomCta;
    const {
      variant: ctaVariant,
      iconColor: ctaIconColor,
      isGhostCta,
    } = getCtaConfig({
      ctaVariant: item.ctaVariant,
      isDarkBackground,
      isMobile,
      size,
    });

    // CDS's own focus box-shadow doesn't survive being rendered through PreservedUtmButton
    // (an anchor), so it's re-added explicitly here
    const ctaFocusStyle = ctaVariant === 'primary' ? styles.primaryButtonFocusStyle : styles.secondaryButtonFocusStyle;

    const customBackgroundStyle = (parsed?: ReturnType<typeof getCustomBackgroundStyle>) =>
      parsed ? `${parsed.property}: ${parsed.value}; border: none;` : null;

    const cardStyles = [
      styles.card,
      size === CARD_SIZE.LARGE && styles.largeCardMd,
      cardColorStyle(cardBackgroundColor),
      cardBorderStyle,
      customBackgroundStyle(customBgParsed),
    ].filter(Boolean);

    const renderBackgroundImage = () => {
      if (!backgroundImage?.url) return null;

      if (isOneCardCannedCollectionModule) {
        return (
          <div css={styles.oneCardModuleBackgroundImageContainer}>
            <div css={styles.oneCardModuleBackgroundImage}>
              <ResponsiveImage
                images={[
                  {
                    url: item.cardBackgroundImage?.url,
                    width: item.cardBackgroundImage?.width,
                    height: item.cardBackgroundImage?.height,
                    alt: item.cardBackgroundImage?.description,
                    media: `(min-width: ${breakpoints.values.sm}px)`,
                    isFallback: true,
                  },
                  {
                    url: item.mobileCardBackgroundImage?.url,
                    width: item.mobileCardBackgroundImage?.width,
                    height: item.mobileCardBackgroundImage?.height,
                    alt: item.mobileCardBackgroundImage?.description,
                    media: `(max-width: ${breakpoints.values.sm - 1}px)`,
                  },
                ]}
                fetchPriority="auto"
              />
            </div>
          </div>
        );
      }

      if (shouldShowImageInFull) {
        return (
          <div css={styles.backgroundImageContainer}>
            <div css={styles.backgroundImageFull(cards.length)}>
              <Image {...backgroundImage} shouldPreload={preloadImage} shouldLazyLoad={lazyLoadImage} />
            </div>
          </div>
        );
      }

      // Default behavior
      return (
        <div css={styles.backgroundImage}>
          <div
            css={styles.backgroundImageInner(
              backgroundImage?.width
                ? Math.max(BACKGROUND_IMAGE_LEFT_OFFSET * 2 - backgroundImage?.width, BACKGROUND_IMAGE_LEFT_OFFSET)
                : BACKGROUND_IMAGE_LEFT_OFFSET,
              isRtl,
              isOneCardCannedCollectionModule
            )}
          >
            <Image
              {...backgroundImage}
              style={{
                maxWidth: 'unset',
              }}
              shouldPreload={preloadImage}
              shouldLazyLoad={lazyLoadImage}
            />
          </div>
        </div>
      );
    };

    const getContentStyles = () => {
      const base = shouldShowImageInFull
        ? [cardSizeStyle, styles.cardContent(false), styles.cardContentImageInFull]
        : [cardSizeStyle, styles.cardContent(!!backgroundImage?.url)];
      return isWholeCardRenderedAsLink ? [...base, styles.cardInteractionProxy] : base;
    };

    const contents = (
      <div
        css={[
          styles.cardContentWrapper,
          !isOneCardCannedCollectionModule && shouldShowImageInFull && styles.cardContentFull(cards.length),
          isOneCardCannedCollectionModule && styles.oneCardModuleContent,
        ]}
      >
        <div css={getContentStyles()}>
          <div>
            {(item.eyebrow || item.eyebrowImage) && (
              <div
                css={[
                  size === CARD_SIZE.LARGE ? styles.eyebrowSpacingLarge : styles.eyebrowSpacing,
                  isDarkBackground ? styles.eyebrowColorInvert : styles.eyebrowColor,
                ]}
                data-testid="eyebrow-image"
              >
                <Eyebrow
                  text={item.eyebrow}
                  background={item.eyebrowBackground}
                  image={item.eyebrowImage}
                  eyebrowImageHeight={item.eyebrowImageHeight}
                />
              </div>
            )}
            {item.headline && (
              <Typography2
                component="h3"
                variant={cardTitleVariant}
                css={size === CARD_SIZE.LARGE ? styles.cardTitleLarge : styles.cardTitle}
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {isWholeCardRenderedAsLink ? (
                  <span
                    ref={(el) => {
                      const anchor = el?.querySelector('a') ?? null;
                      if (anchor) {
                        headingLinkRefs.current.set(item.id, anchor);
                      } else {
                        headingLinkRefs.current.delete(item.id);
                      }
                    }}
                  >
                    <PreservedUtmLink
                      href={item.target}
                      openInNewTab={item.openInNewTab ?? true}
                      onClick={item.onClick}
                      trackingName="canned_collection_card"
                      css={[styles.stretchedOverlay, styles.headingLink]}
                      {...(item.ctaText ? { 'aria-describedby': `cta-${item.id}` } : {})}
                    >
                      {item.headline}
                    </PreservedUtmLink>
                  </span>
                ) : (
                  item.headline
                )}
              </Typography2>
            )}
            {item.body && (
              <Typography2
                variant={cardSubtitleVariant}
                component="div"
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {item.body}
              </Typography2>
            )}
          </div>
          {hasCustomCta ? (
            <div css={[styles.customCtaWrapper, size === CARD_SIZE.LARGE && styles.ctaLarge]}>
              {item.renderCustomPrimaryCta?.({
                styleOverrides: [styles.stretchedOverlay, ctaFocusStyle],
                ctaIcon: <ArrowNextIcon color={ctaIconColor} />,
              })}
            </div>
          ) : (
            item.ctaText && (
              <div>
                <Button
                  css={[
                    styles.cta,
                    size === CARD_SIZE.LARGE && styles.ctaLarge,
                    isGhostCta && styles.ghostCta,
                    item.target && styles.decorativeCta,
                  ]}
                  size="small"
                  variant={ctaVariant}
                  icon={<ArrowNextIcon color={ctaIconColor} />}
                  iconPosition="after"
                  {...(item.target
                    ? {
                        'aria-hidden': true,
                        tabIndex: -1,
                        id: `cta-${item.id}`,
                        onClick: () => {
                          headingLinkRefs.current.get(item.id)?.click();
                        },
                      }
                    : {})}
                >
                  {item.ctaText}
                </Button>
              </div>
            )
          )}
        </div>
        {renderBackgroundImage()}
      </div>
    );

    return (
      <TrackedBasicCard
        eventingData={{
          merchandisingModule: {
            moduleType: 'canned_collection_module',
            heading: item.headline,
            // Guard against a nullable Contentful value reaching the event: the eventing schema
            // allows `body` to be absent but rejects an explicit null (expects String, found Null).
            body: item.body ?? undefined,
            ...item.eventingData?.merchandisingModule,
            index: itemIndex,
          },
        }}
        cardStyles={cardStyles}
      >
        {contents}
      </TrackedBasicCard>
    );
  };

  let numDisplayedRows = size === CARD_SIZE.SMALL ? 2 : 1;
  if (showAllEntries) {
    numDisplayedRows = cards.length;
  }

  /**
   * This is a workaround to handle multiple card layouts on mobile resolutions.
   * The CDS grid component has a breakpoint for XS but no generalized way to configure this
   * and no additional breakpoints for smaller screens. This is noticable on mobile devices.
   *
   * In order to get around this we populate the grid config with xxs as the default to handle
   * initial mobile rendering, and then break to the xxs -> xs resolution after first render.
   *
   * @returns GridConfig
   */
  const getItemsGridConfig = (): GridConfig => {
    const hasThreeOrMoreCards = cards.length >= 3;
    let xsSize: GridConfig['xs'] = 12;
    if (isMobile && showImageInFull && !isXxs) {
      xsSize = isOneCardCannedCollectionModule || hasThreeOrMoreCards ? 12 : 6;
    }

    if (isOneCardCannedCollectionModule) {
      return { xs: xsSize, sm: 12, md: 12 };
    } else if (isGridVariant) {
      return { xs: xsSize, sm: 6, md: 6 };
    } else if (size === CARD_SIZE.LARGE) {
      return { xs: xsSize, sm: 12, md: 6 };
    }

    return { xs: xsSize, sm: hasThreeOrMoreCards ? 12 : 6, md: cards.length === 2 ? 6 : 4 };
  };

  const contentPanel = () => {
    return (
      <div>
        {(heading || subheading) && (
          <div css={styles.headingContainer}>
            {heading && (
              <Typography2 variant="titleSmall" component="h2" color="inherit">
                {heading}
              </Typography2>
            )}
            {subheading && (
              <Typography2 variant="subtitleMedium" component="h3" color="inherit" css={styles.subheading}>
                {subheading}
              </Typography2>
            )}
          </div>
        )}
        <ShowMoreGridSection
          items={cards}
          spacing={isMobile ? 8 : 16}
          itemsGridConfig={getItemsGridConfig()}
          defaultRows={numDisplayedRows}
          renderItem={({ item, itemIndex }) => renderCardContent(item, itemIndex)}
          css={styles.showMoreGridSection}
        />
      </div>
    );
  };

  return <ModuleLayout assembly={noImage(props)} renderContent={contentPanel} ref={ref} />;
});

export default CannedCollectionModule;
